/**
  * Google sign-in via Google Identity Services.
  *
  * This is the browser-only OAuth token flow: there is no client secret, no
  * redirect page and no backend. GIS hands us a short-lived access token that we
  * keep in memory only.
  *
  * Access tokens are deliberately **not** persisted. On reload we ask GIS for a
  * fresh one silently (`prompt: ''`), which succeeds without any UI as long as
  * you are still signed in to Google. That is both nicer than a stored token and
  * safer than parking a Drive credential in localStorage.
  */

export const DRIVE_SCOPES = [
    // Browse and stream the audiobooks. A *restricted* scope: for a personal
    // app, keep the Cloud project on the "Testing" publishing status and add
    // yourself as a test user — Google's personal-use exception covers it, and
    // no verification or security assessment is required.
    'https://www.googleapis.com/auth/drive.readonly',
    // Hidden per-app folder for the progress file. Non-sensitive; invisible in
    // the Drive UI; never touches your own files.
    'https://www.googleapis.com/auth/drive.appdata',
].join(' ');

const GIS_SRC = 'https://accounts.google.com/gsi/client';
const CLIENT_ID_KEY = 'shelf.googleClientId';

interface TokenResponse {
    access_token?: string;
    expires_in?: number;
    scope?: string;
    error?: string;
    error_description?: string;
}

interface TokenClient {
    requestAccessToken(overrides?: { prompt?: string }): void;
}

interface GoogleOAuth2 {
    initTokenClient(config: {
    client_id: string;
    scope: string;
    prompt?: string;
    callback: (response: TokenResponse) => void;
    error_callback?: (error: { type?: string; message?: string }) => void;
  }): TokenClient;
  revoke(token: string, done?: () => void): void;
}

declare global {
   interface Window {
     google?: { accounts?: { oauth2?: GoogleOAuth2 } };
   }
}

/* ---------- client id ---------- */

/** Build-time value wins only as a default; the in-app setting overrides it, so
  * one build can be moved between machines and hosts without recompiling. */
export function getClientId(): string {
   const stored = localStorage.getItem(CLIENT_ID_KEY)?.trim();
   if (stored) return stored;
   return (import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '').trim();
}

export function setClientId(id: string): void {
   const trimmed = id.trim();
   if (trimmed) localStorage.setItem(CLIENT_ID_KEY, trimmed);
   else localStorage.removeItem(CLIENT_ID_KEY);
}

export function hasClientId(): boolean {
   return getClientId().length > 0;
}

/* ---------- script loading ---------- */

let scriptPromise: Promise<void> | null = null;

function loadGis(): Promise<void> {
   if (window.google?.accounts?.oauth2) return Promise.resolve();
   if (scriptPromise) return scriptPromise;

   scriptPromise = new Promise((resolve, reject) => {
     const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SRC}"]`);
     const script = existing ?? document.createElement('script');
     script.src = GIS_SRC;
     script.async = true;
     script.defer = true;
     script.addEventListener('load', () => resolve());
     script.addEventListener('error', () =>
        reject(
           new Error(
              'Could not load Google sign-in. Check your connection, and any network ' +
                'filtering that might block accounts.google.com.',
           ),
        ),
     );
     if (!existing) document.head.appendChild(script);
   });

   return scriptPromise;
}

/* ---------- token ---------- */

let accessToken: string | null = null;
let expiresAt = 0;
let client: TokenClient | null = null;
let clientIdInUse = '';
let inflight: Promise<string> | null = null;

const listeners = new Set<(token: string | null) => void>();

/** Notified whenever the token appears or disappears — the service worker
  * needs to be kept in step. */
export function onTokenChange(fn: (token: string | null) => void): () => void {
   listeners.add(fn);
   return () => listeners.delete(fn);
}

function publish(token: string | null): void {
   accessToken = token;
   for (const fn of listeners) fn(token);
}

export function isSignedIn(): boolean {
    return accessToken !== null && Date.now() < expiresAt;
}

export function currentToken(): string | null {
    return isSignedIn() ? accessToken : null;
}

async function ensureClient(): Promise<TokenClient> {
    const clientId = getClientId();
    if (!clientId) {
      throw new Error(
         'No Google OAuth Client ID set yet. Add one in Settings — see the README for the ' +
           'five-minute Google Cloud setup.',
      );
    }

    await loadGis();
    const oauth2 = window.google?.accounts?.oauth2;
    if (!oauth2) throw new Error('Google sign-in loaded but did not initialise.');

    if (client && clientIdInUse === clientId) return client;

    clientIdInUse = clientId;
    client = oauth2.initTokenClient({
      client_id: clientId,
      scope: DRIVE_SCOPES,
      callback: () => {
         /* replaced per-request in requestToken */
      },
    });
    return client;
}

/**
  * Returns a usable access token, requesting one if needed.
  *
  * `interactive: false` attempts a silent refresh and is what runs on page load
  * and when a token expires mid-listen. `interactive: true` is the explicit
  * "Connect Google Drive" button and may show the account chooser and consent.
  */
export async function getAccessToken({ interactive = false } = {}): Promise<string> {
    if (isSignedIn() && accessToken) return accessToken;
    if (inflight) return inflight;

    inflight = requestToken(interactive).finally(() => {
      inflight = null;
    });
    return inflight;
}

function requestToken(interactive: boolean): Promise<string> {
    return new Promise((resolve, reject) => {
      // ensureClient loads GIS and validates the Client ID; the actual client is
      // rebuilt below so this request gets its own callbacks.
      void ensureClient()
         .then(() => {
           const oauth2 = window.google?.accounts?.oauth2;
           if (!oauth2) return reject(new Error('Google sign-in unavailable.'));

           // The callback is per-request, so re-init with fresh handlers each time.
           const configured = oauth2.initTokenClient({
             client_id: clientIdInUse,
             scope: DRIVE_SCOPES,
             callback: (response) => {
                if (response.error || !response.access_token) {
                  publish(null);
                  reject(
                     new Error(
                        describeAuthError(response.error, response.error_description, interactive),
                     ),
                  );
                  return;
                }
                expiresAt = Date.now() + (response.expires_in ?? 3600) * 1000 - 60_000;
                publish(response.access_token);
                resolve(response.access_token);
             },
             error_callback: (err) => {
                publish(null);
                reject(new Error(describeAuthError(err.type, err.message, interactive)));
             },
           });

        client = configured;
        // An empty prompt means "do not show UI unless you have to".
        configured.requestAccessToken({ prompt: interactive ? 'consent' : '' });
      })
      .catch(reject);
  });
}

function describeAuthError(
  code: string | undefined,
  detail: string | undefined,
  interactive: boolean,
): string {
  switch (code) {
    case 'popup_closed':
    case 'popup_failed_to_open':
      return 'The Google sign-in window was closed before finishing.';
    case 'access_denied':
      return 'Access was declined. Shelf needs read access to Drive to list and play your books.';
    case 'idpiframe_initialization_failed':
      return 'Google sign-in could not start — third-party cookies may be blocked.';
    default:
      if (!interactive) {
        return 'Not signed in to Google. Use "Connect Google Drive" in Settings.';
      }
      return detail ?? code ?? 'Google sign-in failed.';
  }
}

export function signOut(): void {
  const token = accessToken;
  publish(null);
  expiresAt = 0;
  if (token) window.google?.accounts?.oauth2?.revoke(token);
}

/** Called after a 401 so the next request fetches a brand-new token. */
export function invalidateToken(): void {
  expiresAt = 0;
  publish(null);
}
